import { fmtDate } from "@/lib/formatDate";
// ============================================================================
// GLOBAL SMART ALERTS — data layer (real FastAPI + MongoDB backend, Phase 7)
// ----------------------------------------------------------------------------
// Regole globali (read = qualsiasi utente interno; write = manage_settings/admin).
// Stato dismiss/snooze + eventi persistiti per-utente. Popup engine resta
// client-side. Store backend-hydrated + mutazioni ottimistiche (API sincrona
// invariata). NO notifiche esterne / scheduling server / n8n.
// ============================================================================

import { listTeam } from "@/lib/teamData";
import { api } from "@/lib/api";

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
const pad2 = (n) => String(n).padStart(2, "0");

export const alertUsers = () => listTeam().filter((u) => u.role !== "client_user").map((u) => ({ id: u.id, name: u.name, initials: u.initials, avatar: u.avatar, role: u.role }));
export const alertUser = (id) => alertUsers().find((u) => u.id === id) || null;

export const ALERT_CATEGORIES = {
  campaigns: { label: "Campagne", icon: "Megaphone", tone: "info" },
  monthly_calls: { label: "Call Mensili", icon: "PhoneCall", tone: "info" },
  editorial_plans: { label: "Piani Editoriali", icon: "CalendarCheck", tone: "warning" },
  accounting: { label: "Contabilità", icon: "Wallet", tone: "warning" },
  administration: { label: "Amministrazione", icon: "FileText", tone: "neutral" },
  crm: { label: "CRM", icon: "Users", tone: "info" },
  support_tickets: { label: "Ticket di supporto", icon: "LifeBuoy", tone: "warning" },
  calendar: { label: "Calendario", icon: "Calendar", tone: "info" },
  general: { label: "Operazioni Generali", icon: "ClipboardList", tone: "neutral" },
};

export const ALERT_PRIORITIES = {
  low: { label: "Bassa", tone: "neutral" },
  medium: { label: "Media", tone: "info" },
  high: { label: "Alta", tone: "warning" },
  critical: { label: "Critica", tone: "error" },
};

export const TRIGGER_TYPES = {
  specific_date: "Data specifica", day_of_month: "Giorno del mese", date_range: "Intervallo di giorni",
  month_range: "Intervallo di mesi", yearly: "Annuale", weekly: "Settimanale", random: "Casuale",
  recurring_interval: "Intervallo ricorrente", manual: "Solo manuale",
};

export const RANGE_FREQUENCY = { once: "Una volta", daily: "Ogni giorno", every_2: "Ogni 2 giorni", weekly: "Ogni settimana" };
export const RANDOM_FREQUENCY = { once_month: "Una volta al mese", twice_month: "Due volte al mese", once_week: "Una volta a settimana" };
export const TARGET_TYPES = {
  everyone: "Tutti", admins: "Solo Admin", internal_team: "Solo team interno",
  specific_users: "Membri specifici", assigned_team_members: "Solo membri assegnati",
};

export const RELATED_MODULES = {
  none: { label: "Nessuno", path: null },
  dashboard: { label: "Dashboard", path: "/" },
  tasks: { label: "Tasks", path: "/tasks/operations" },
  monthly_operations: { label: "Operazioni Mensili", path: "/tasks/operations" },
  editorial_planner: { label: "Editorial Planner", path: "/tasks/editorial" },
  support_tickets: { label: "Ticket di supporto", path: "/tasks/support-tickets" },
  accounting: { label: "Contabilità", path: "/contabilita" },
  administration: { label: "Amministrazione", path: "/amministrazione" },
  crm: { label: "CRM", path: "/crm" },
  calendar: { label: "Calendario", path: "/calendar" },
  client_registry: { label: "Anagrafica Clienti", path: "/clienti" },
  lead_router: { label: "Lead Router", path: "/lead-router" },
  password: { label: "Password", path: "/password" },
};

export const SNOOZE_OPTIONS = {
  "1h": { label: "1 ora", ms: 60 * 60 * 1000 },
  today: { label: "Più tardi oggi", ms: 3 * 60 * 60 * 1000 },
  tomorrow: { label: "Domani", ms: 24 * 60 * 60 * 1000 },
  next_week: { label: "Prossima settimana", ms: 7 * 24 * 60 * 60 * 1000 },
};

export const DAYS_OF_WEEK = [
  { v: 1, label: "Lunedì" }, { v: 2, label: "Martedì" }, { v: 3, label: "Mercoledì" },
  { v: 4, label: "Giovedì" }, { v: 5, label: "Venerdì" }, { v: 6, label: "Sabato" }, { v: 0, label: "Domenica" },
];
export const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export const ACTION_LABELS = { shown: "Mostrato", dismissed: "Ignorato", snoozed: "Posticipato", opened: "Modulo aperto" };

// ----------------------------------------------------------------------------
// CACHE (backend-hydrated)
// ----------------------------------------------------------------------------
const empty = () => ({ rules: [], events: [], state: {} });
let _store = empty();
let _hydrated = false;
let _userId = null;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeAlerts = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isAlertsReady = () => _hydrated;

export async function hydrateAlerts(userId) {
  _userId = userId || _userId;
  try {
    const { data } = await api.get("/alerts/store");
    _store = { ...empty(), ...data };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("hydrateAlerts failed", e?.response?.status);
  } finally { _hydrated = true; notify(); }
}
export const resetAlerts = () => { _store = empty(); _hydrated = false; _userId = null; };
const resync = () => { hydrateAlerts(); };
const read = () => _store;
const touch = () => { _store = { ..._store }; notify(); };
const persist = (promise) => { promise.catch(resync); };

// ----------------------------------------------------------------------------
// Rules CRUD
// ----------------------------------------------------------------------------
export const listRules = () => read().rules.slice().sort((a, b) => Number(b.is_active) - Number(a.is_active) || (a.alert_name || "").localeCompare(b.alert_name || ""));
export const getRule = (id) => read().rules.find((r) => r.id === id) || null;

export const emptyRule = () => ({
  alert_name: "", alert_title: "", alert_message: "", category: "general", priority: "medium",
  trigger_type: "day_of_month", trigger_config: { day: 1, time: "09:00" },
  target_type: "everyone", target_user_ids: [],
  related_module: "none", button_label: "", button_action: "", is_active: true,
});

export const createRule = (data) => {
  const id = uid("alert");
  const rule = { ...emptyRule(), ...data, id, created_by: "Andrea Ferrari", created_at: now(), updated_at: now(), last_shown: null };
  _store.rules = [rule, ..._store.rules]; touch();
  persist(api.post("/alerts/rules", { ...rule }).then(({ data: d }) => { _store.rules = _store.rules.map((r) => (r.id === id ? d : r)); touch(); }));
  return id;
};
export const updateRule = (id, patch) => { _store.rules = _store.rules.map((r) => (r.id === id ? { ...r, ...patch, updated_at: now() } : r)); touch(); persist(api.patch(`/alerts/rules/${id}`, patch)); };
export const setRuleActive = (id, active) => updateRule(id, { is_active: active });
export const duplicateRule = (id) => {
  const r = getRule(id); if (!r) return null;
  return createRule({ ...JSON.parse(JSON.stringify(r)), id: undefined, alert_name: `${r.alert_name} (copia)`, is_active: false });
};
export const removeRule = (id) => { _store.rules = _store.rules.filter((r) => r.id !== id); touch(); persist(api.delete(`/alerts/rules/${id}`)); };

// ----------------------------------------------------------------------------
// Events
// ----------------------------------------------------------------------------
export const listEvents = () => read().events.slice().sort((a, b) => new Date(b.shown_at || b.created_at) - new Date(a.shown_at || a.created_at));
export const logEvent = ({ alert_rule_id, user_id, action, snoozed_until = null, opened_module = null }) => {
  const t = now();
  const local = { id: uid("evt"), alert_rule_id, user_id: user_id || _userId, shown_at: t, action_taken: action, snoozed_until, dismissed_at: action === "dismissed" ? t : null, opened_module, created_at: t };
  _store.events = [local, ..._store.events].slice(0, 200);
  const i = _store.rules.findIndex((r) => r.id === alert_rule_id);
  if (i > -1 && action === "shown") _store.rules[i] = { ..._store.rules[i], last_shown: t };
  touch();
  persist(api.post("/alerts/events", { alert_rule_id, action, snoozed_until, opened_module }));
};

// ----------------------------------------------------------------------------
// Per-user occurrence state (dismiss / snooze)
// ----------------------------------------------------------------------------
const stateKey = (ruleId, userId) => `${ruleId}::${userId}`;
export const getRuleUserState = (ruleId, userId) => read().state[stateKey(ruleId, userId)] || {};
export const setDismissed = (ruleId, userId, occKey) => {
  _store.state = { ..._store.state, [stateKey(ruleId, userId)]: { ...(_store.state[stateKey(ruleId, userId)] || {}), dismissedOcc: occKey, snoozedUntil: null } }; touch();
  persist(api.post(`/alerts/state/${ruleId}/dismiss`, { occ_key: occKey }));
};
export const setSnoozed = (ruleId, userId, untilISO) => {
  _store.state = { ..._store.state, [stateKey(ruleId, userId)]: { ...(_store.state[stateKey(ruleId, userId)] || {}), snoozedUntil: untilISO } }; touch();
  persist(api.post(`/alerts/state/${ruleId}/snooze`, { until: untilISO }));
};

// ----------------------------------------------------------------------------
// Schedule engine (pure, client-side)
// ----------------------------------------------------------------------------
const daysBetween = (a, b) => Math.floor((a - b) / 86400000);

const matchesDay = (rule, d) => {
  const c = rule.trigger_config || {};
  const dom = d.getDate(), mon = d.getMonth() + 1, dow = d.getDay();
  switch (rule.trigger_type) {
    case "specific_date": {
      if (!c.date) return false; const t = new Date(c.date);
      return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
    }
    case "day_of_month": return dom === Number(c.day);
    case "date_range": return dom >= Number(c.start_day || 1) && dom <= Number(c.end_day || 31);
    case "month_range": {
      const inMonths = Number(c.start_month) <= Number(c.end_month)
        ? mon >= Number(c.start_month) && mon <= Number(c.end_month)
        : mon >= Number(c.start_month) || mon <= Number(c.end_month);
      return inMonths && dom === Number(c.day || 1);
    }
    case "yearly": return mon === Number(c.month) && dom === Number(c.day);
    case "weekly": return dow === Number(c.day_of_week);
    case "random": return dom >= Number(c.start_day || 1) && dom <= Number(c.end_day || 31);
    case "recurring_interval": {
      if (!c.start_date) return false;
      const start = new Date(c.start_date); start.setHours(0, 0, 0, 0);
      const dd = new Date(d); dd.setHours(0, 0, 0, 0);
      if (dd < start) return false;
      if (c.end_date && dd > new Date(c.end_date)) return false;
      const n = Number(c.every_days || 1);
      return n > 0 && daysBetween(dd, start) % n === 0;
    }
    default: return false; // manual
  }
};

const timeOf = (rule) => {
  const t = (rule.trigger_config || {}).time || "09:00";
  const [h, m] = t.split(":").map(Number);
  return { h: h || 0, m: m || 0 };
};

export const occurrenceKey = (rule, d = new Date()) => `${rule.trigger_type}-${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const isDueNow = (rule, nowDate = new Date()) => {
  if (!rule.is_active || rule.trigger_type === "manual") return false;
  if (!matchesDay(rule, nowDate)) return false;
  const { h, m } = timeOf(rule);
  const due = new Date(nowDate); due.setHours(h, m, 0, 0);
  return nowDate >= due;
};

export const nextOccurrence = (rule, from = new Date()) => {
  if (rule.trigger_type === "manual") return null;
  const { h, m } = timeOf(rule);
  const start = new Date(from); start.setHours(0, 0, 0, 0);
  for (let i = 0; i < 400; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    if (matchesDay(rule, d)) {
      const cand = new Date(d); cand.setHours(h, m, 0, 0);
      if (cand >= from) return cand;
    }
  }
  return null;
};

export const describeSchedule = (rule) => {
  const c = rule.trigger_config || {};
  const time = c.time ? ` alle ${c.time}` : "";
  switch (rule.trigger_type) {
    case "specific_date": return c.date ? `Il ${fmtDate(c.date)}${time}` : "Data non impostata";
    case "day_of_month": return `Ogni mese il giorno ${c.day}${time}`;
    case "date_range": return `Giorni ${c.start_day}-${c.end_day} · ${RANGE_FREQUENCY[c.frequency] || "una volta"}${time}`;
    case "month_range": return `${MONTHS[(c.start_month || 1) - 1]}–${MONTHS[(c.end_month || 1) - 1]}, giorno ${c.day}${time}`;
    case "yearly": return `Ogni anno il ${c.day} ${MONTHS[(c.month || 1) - 1]}${time}`;
    case "weekly": { const d = DAYS_OF_WEEK.find((x) => x.v === Number(c.day_of_week)); return `Ogni ${d ? d.label : "settimana"}${time}`; }
    case "random": return `Casuale, giorni ${c.start_day}-${c.end_day} · ${RANDOM_FREQUENCY[c.frequency] || "una volta al mese"}`;
    case "recurring_interval": return `Ogni ${c.every_days} giorni${time}`;
    default: return "Solo manuale";
  }
};

const targetMatches = (rule, user) => {
  if (!user) return false;
  const internal = user.role !== "client_user";
  switch (rule.target_type) {
    case "everyone": return internal;
    case "admins": return user.role === "admin";
    case "internal_team": return internal;
    case "assigned_team_members": return internal;
    case "specific_users": return (rule.target_user_ids || []).includes(user.id);
    default: return internal;
  }
};

export const getDueAlerts = (user, nowDate = new Date()) => {
  return listRules().filter((rule) => {
    if (!targetMatches(rule, user)) return false;
    if (!isDueNow(rule, nowDate)) return false;
    const st = getRuleUserState(rule.id, user.id);
    const occ = occurrenceKey(rule, nowDate);
    if (st.dismissedOcc === occ) return false;
    if (st.snoozedUntil && new Date(st.snoozedUntil) > nowDate) return false;
    return true;
  });
};

export const getDemoAlerts = () => read().rules.filter((r) => r.is_active).slice(0, 2);

export const alertKpis = () => {
  const rules = read().rules;
  return {
    total: rules.length,
    active: rules.filter((r) => r.is_active).length,
    inactive: rules.filter((r) => !r.is_active).length,
    events: read().events.length,
  };
};
